'use client';

import { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { useModals } from '@mattjennings/react-modal-stack';
import {
  usePayOrderMutation,
  useVerifyPaymentStatusMutation,
  useConfirmChallengeMutation,
} from '@/store/checkout/checkout.api';
import { useSaveBookingMutation } from '@/store/restore-booking/restoreBooking.api';
import * as Sentry from '@sentry/nextjs';
import useCallApiProcess from '../useCallApiProcess';
import useOpenPay from '../useOpenPay';
import useStepper from './useStepper';
import {
  useAuth,
  useBayonet,
  useRsvp,
  useEvent,
  useBooking,
  useAddOns,
  useMsci,
  usePayment,
  useCustomLinksActions,
} from '@/store';
import { separateNames } from '@/utils/miscellaneous';
import useAnalyticsEvents from '@/hooks/useAnalyticsEvents';
import getSpredlyToken from '../useSpreedly';
import usePaymentGateway from '../usePaymentGateway';
import useAddOnsControl from '@/sections/addons/useAddOnsControl';
import useOrkestaPayCard from './useOrkestaPay';
import useUserTracking from '../userTracking';
import useStore from '@/store/hooks/useStore';
import ThreeDSecureRedirectionDialog from '@/components/dialogs/IconDialog';

const MAX_RETRIES = 120;

export default function useCheckoutPayBooking() {
  const [paymentStatus, setPaymentStatus] = useState(null);
  const { t } = useTranslation();
  const router = useRouter();
  const { handleTicketInsuranceHealthy } = useAddOnsControl();

  const { createToken } = useOrkestaPayCard();

  const [confirmChallengeMut] = useConfirmChallengeMutation();

  const [saveBookingInfo] = useSaveBookingMutation();

  const { openModal } = useModals();

  const { getStoreCopy } = useStore();

  const {
    event: { id, name },
    preferences: { fee_breakdown, currency },
  } = useSelector((state) => state.event);
  const booking = useSelector((state) => state.booking);
  const surveys = useSelector((state) => state.surveys);
  const promotions = useSelector((state) => state.promotions);
  const msi = useSelector((state) => state.msi);
  const summary = useSelector((state) => state.summary, shallowEqual);
  const ticketInsurance = useSelector((state) => state.addons.ticketInsurance);

  const { changeIsInChallengeStatus, isInChallenge, setAttemptsCount } =
    usePayment();

  const {
    booking: { order },
  } = useBooking();

  const {
    customLink: { link },
  } = useCustomLinksActions();

  const {
    event: { gateway },
  } = useEvent();

  const {
    payment: paymentEvent,
    purchase,
    reserveTickets,
  } = useAnalyticsEvents();

  const {
    msci: { selected = null, selectedBin = '' },
    resetPlans,
  } = useMsci();

  const { addOnsActive } = useAddOns();

  const { method: payment } = useSelector(
    (state) => state.payment,
    shallowEqual,
  );

  const {
    purchase: purchaseTrackingEvent,
    flowStarted,
    redirectInitiated,
    returnedFromBank,
    challengeFailed,
    challengeSuccess,
  } = useUserTracking();

  const { nextStep } = useStepper();

  const [verifyPaymentStatusRequest] = useVerifyPaymentStatusMutation();

  const [payOrderRequest] = usePayOrderMutation();

  const { callApi, alert, confirm, showBackdrop, confirmBackCode, closeModal } =
    useCallApiProcess(
      payOrderRequest,
      t('checkout.loadings.processingPayment'),
    );

  const { callApi: callApiOxxo } = useCallApiProcess(
    payOrderRequest,
    t('checkout.loadings.creatingOxxo'),
  );
  const { callApi: callApiFreePay } = useCallApiProcess(
    payOrderRequest,
    t('checkout.loadings.loadingTickets'),
  );

  const { bayonet } = useBayonet();
  const { auth, isGuest } = useAuth();
  const { rsvpStored: rsvp } = useRsvp();

  const { deviceSessionId } = useOpenPay();

  const { getPaymentGateway } = usePaymentGateway();
  const sendPuchaseEvent = (oxxo = false) => {
    let ticket_ty = [];
    let paymentMethod = Object.keys(payment)[0];

    for (const iterator of booking.orderInfo.tickets) {
      let findType = ticket_ty.find((t) => t === iterator.ticket_type_name);
      if (!findType) ticket_ty.push(iterator.ticket_type_name);
    }

    if (oxxo) {
      reserveTickets({
        event: id,
        currency,
        value: summary.total_to_pay,
      });
    } else {
      purchase({
        event: id,
        currency,
        value: summary.total_to_pay,
        total: summary.total_to_pay,
        ticket_insurance: ticketInsurance.selected,
      });
    }
  };

  const getPaymentName = () => {
    if (payment['Gateway::Banwire'] && payment['Gateway::Banwire'].valid) {
      return separateNames(payment['Gateway::Banwire'].creditCard.name);
    }
    const custom = surveys.custom || surveys.tickets;
    if (custom) {
      const survey = Object.values(custom);
      if (survey.length > 0) {
        if (survey[0].name) {
          return separateNames(survey[0].name);
        }
      }
    }
    return 'invite user';
  };

  const getCustomer = () => {
    if (auth?.token?.role === 'owner' || auth?.token?.role === 'seller') {
      const { customer } = booking;
      return {
        user_id: 'userID',
        name: customer.name,
        last_name: customer.lastName,
        phone_number: `${customer.dialCode}${customer.phone}`,
        email: customer.email,
        zip_code: payment['Gateway::Banwire']
          ? payment['Gateway::Banwire'].creditCard?.zipCode
          : null,
      };
    }

    const { name, lastName } = getPaymentName();
    if (isGuest) {
      return {
        user_id: auth.token ? auth.token.user_info.user_id : 'invite_user',
        name,
        last_name: lastName || name,
        phone_number: auth.user.phone,
        email: auth.user.email,
        zip_code: payment['Gateway::Banwire']
          ? payment['Gateway::Banwire'].creditCard?.zipCode
          : null,
      };
    }
    if (auth.user) {
      return {
        user_id: auth.token ? auth.token.user_info.user_id : '',
        name: auth.token ? auth.token.user_info.frist_name : name,
        last_name:
          auth.token?.user_info.last_name || auth.token?.user_info.frist_name,
        phone_number: auth.token ? auth.token.user_info.phone_number : '',
        email: auth.user.email,
        zip_code: payment['Gateway::Banwire']
          ? payment['Gateway::Banwire'].creditCard?.zipCode
          : null,
      };
    }
    return {
      user_id: 'invite_user',
      name: 'invite',
      last_name: 'invite',
      phone_number: '+525555555555',
      email: 'email@boletia.com',
    };
  };

  const verifyPaymentStatus = async () => {
    try {
      const response = await verifyPaymentStatusRequest(
        router.query?.order || booking.order,
      );
      return response;
    } catch {
      return false;
    }
  };

  const waitAndCheckStatus = () =>
    new Promise((resolve, reject) => {
      setTimeout(() => {
        verifyPaymentStatus()
          .then((resp) => {
            resolve(resp.data);
          })
          .catch((e) => {
            reject({ error: 'Error en el servidor' });
          });
      }, 1000);
    });

  /**
   * Monitors the payment status using a long polling technique.
   *
   * The polling logic is controlled by the `skipUrl` parameter to handle 3DS authentication flows:
   *
   * 1. **Normal Flow (skipUrl = true):** Polling continues until the status is no longer 'paying-gateway'.
   *    A URL in the response (e.g., for a 3DS challenge) is ignored.
   *
   * 2. **Initial 3DS Flow (skipUrl = false):** Polling stops EITHER when the status changes OR if a URL is received.
   *    This allows the application to redirect the user to the 3DS challenge page.
   *    In this flow we have a max retries number defined by maxRetries.
   *
   *
   * This distinction is crucial because after the user completes the 3DS challenge, we must continue polling until the final status is received,
   * ignoring any subsequent URLs.
   *
   * @param {boolean} skipUrl - Determines if the presence of a URL should stop the polling. If `false`, a URL will halt polling.
   * @returns {Promise<{status: string, url: string}>} - Resolves with the final status and optional URL.
   */

  const checkPaymentStatus = async (skipUrl = false) => {
    let status = 'paying-gateway';
    let url = '';
    const evaluateUrl = skipUrl;

    let counter = 0;

    do {
      if (skipUrl) {
        counter++;

        if (counter >= MAX_RETRIES) {
          status = 'notCompleted';
          break;
        }
      }

      const resp = await waitAndCheckStatus();
      // paying | paying-gateway | complete
      if (resp.status === 'failed' || resp.status === 'paying')
        status = 'notCompleted';

      if (resp.status === 'complete') status = 'complete';

      if (resp.payer_authentication_url && !skipUrl) {
        console.log('starting challenge');
        changeIsInChallengeStatus(true);
        status = 'need3DsChallenge';
        url = resp.payer_authentication_url;
      }
    } while (status === 'paying-gateway' && !url);

    closeModal();

    return {
      status,
      url,
    };
  };

  const getBaseBody = ({
    method,
    paymentMethodType,
    gateway,
    token,
    deviceID,
    bayonetToken,
    description,
  }) => ({
    event_id: id,
    booking_number: booking.order,
    customer_request: getCustomer(),
    promotion_code: promotions ? promotions.code : null,
    payment_request: {
      payment_method_type: paymentMethodType,
      payment_gateway: gateway,
      fee_breakdown,
      currency,
      method,
      ...(method !== 'oxxo' ? { description: description || '-' } : {}),
      ...(method === 'card'
        ? {
            device_session_id: deviceID,
            source_id: token,
            bayonet_token: bayonetToken,
          }
        : {}),
      ...(rsvp
        ? {
            rsvp: {
              link_id: rsvp.id,
            },
          }
        : {}),
    },
  });

  const paymentCardOrder = async (
    token,
    deviceID,
    payment_gateway = null,
    bayonetToken,
  ) => {
    const body = {
      ...getBaseBody({
        method: 'card',
        paymentMethodType: 'Gateway::Banwire',
        gateway: payment_gateway || 'OPEN_PAY',
        token,
        deviceID,
        bayonetToken,
        description: name,
      }),
      ...(selected
        ? {
            bin: {
              number: String(selectedBin),
              installment: String(selected.payment_plan),
              payment_plan_kind: selected.payment_type,
              selected: true,
            },
          }
        : {}),
      ...(addOnsActive
        ? {
            addons: [
              {
                kind: 'ticket_insurance',
                selected: ticketInsurance.selected,
              },
            ],
          }
        : {}),
    };
    try {
      paymentEvent({
        event: id,
        currency,
        value: summary.total_to_pay,
      });
    } catch {}
    return await payOrderRequest({ body, link });
  };

  const paymentOxxoOrder = async () => {
    const body = {
      ...getBaseBody({
        method: 'oxxo',
        paymentMethodType: 'Gateway::Oxxo',
        gateway: 'OXXO_PAY',
      }),
      ...(addOnsActive
        ? {
            addons: [
              {
                kind: 'ticket_insurance',
                selected: ticketInsurance.selected,
              },
            ],
          }
        : {}),
    };
    try {
      paymentEvent({
        event: id,
        currency,
        value: summary.total_to_pay,
      });
    } catch {}
    return await callApiOxxo({ body, link });
  };

  const paymentCashOrder = async () => {
    const body = {
      ...getBaseBody({
        method: 'cash',
        paymentMethodType: 'PaymentMethod::Cash',
        gateway: 'CASH_PAY',
      }),
    };

    try {
      paymentEvent({
        event: id,
        currency,
        value: summary.total_to_pay,
      });
    } catch {}
    return await callApi({ body, link });
  };

  const payFreeOrder = async () => {
    const body = {
      ...getBaseBody({
        method: 'cash',
        paymentMethodType: 'PaymentMethod::Cash',
        gateway: 'CASH_PAY',
      }),
    };
    try {
      paymentEvent({
        event: id,
        currency,
        value: summary.total_to_pay,
      });
    } catch {}
    return callApiFreePay({ body, link });
  };

  const handleNotCompleted = (type, challengeStatus) => {
    if (type === 'onConfirm') {
      confirm({
        title: 'Verificación de seguridad no completada',
        text: 'No se pudo realizar la verificación de seguridad con tu banco. Para finalizar tu compra, inténtalo nuevamente o utiliza otro método de pago.',
        acceptLabel: 'Entendido',
        omitBooking: true,
      });
      challengeFailed({
        reason:
          challengeStatus === 'abortig'
            ? 'user_cancelled'
            : 'authentication_failed',
      });
    }
    if (type === 'onPayment') {
      if (gateway === 'ADYEN_PAY') window.Spreedly.removeHandlers();
      closeModal();
      confirmBackCode('00012', null);
    }
  };

  const verifyPaymentStatusResponse = async (
    payment,
    type,
    challengeStatus,
  ) => {
    if (payment.status === 'need3DsChallenge') {
      openModal(ThreeDSecureRedirectionDialog, {
        onTimerFinish: () => {
          redirectInitiated();
          window.location.href = payment.url;
        },
        onAbort: async () => {
          changeIsInChallengeStatus(false);
          confirmChallengeMut({
            challengeStatus: 'aborting',
            id: booking.order,
            gateway,
          });
          closeModal();
        },
        onInit: () => {
          try {
            const result = saveBookingInfo({
              booking_number: order,
              local_storage: getStoreCopy(),
            });

            if (result?.error) {
              Sentry.withScope((scope) => {
                scope.setTag('operation', 'saveBookingInfo');
                scope.setTag('booking_number', order);
                scope.setExtra('error', result.error);
                scope.setLevel('error');
                Sentry.captureMessage(
                  'Failed to save booking info before 3DS challenge',
                );
              });
            }
          } catch (error) {
            Sentry.withScope((scope) => {
              scope.setTag('operation', 'saveBookingInfo');
              scope.setTag('booking_number', order);
              scope.setExtra('error', error.message);
              scope.setExtra('errorStack', error.stack);
              scope.setLevel('error');
              Sentry.captureException(error);
            });
          }
          flowStarted();
        },
      });
    }

    if (payment.status === 'notCompleted') {
      handleNotCompleted(type, challengeStatus);
    }

    if (payment.status === 'complete') {
      if (type === 'onConfirm') {
        challengeSuccess();
      }
      sendPuchaseEvent();
      purchaseTrackingEvent({ type: 'paid', method: 'card' });
      nextStep();
    }
  };

  /*
   * Confirm challenge
   * @param {string} challengeStatus - The status of the challenge 'completed' | 'aborting'
   *
   */
  const confirmChallenge = async (challengeStatus) => {
    showBackdrop();
    await confirmChallengeMut({
      challengeStatus,
      id: router.query.order || booking.order,
      gateway,
    });

    // make a long pulling with challenge evaluation rules

    const payment = await checkPaymentStatus(true); // => completed | notCompleted | need3dsChallenge | 3dsNotCompletedOrFailed
    closeModal();
    changeIsInChallengeStatus(false);
    verifyPaymentStatusResponse(payment, 'onConfirm', challengeStatus);
  };

  const payBooking = async () => {
    if (router.pathname === '/payment') {
      setAttemptsCount();
      try {
        if (summary.total_to_pay === 0) {
          const resp = await payFreeOrder();
          if (!resp.error) {
            sendPuchaseEvent();
            purchaseTrackingEvent({ type: 'free', method: 'none' });
            nextStep();
          }
          return;
        }

        if (payment['Gateway::Banwire']) {
          let payToken, sessionId;
          const payment_gateway = await getPaymentGateway();

          const { bayonetFingerprintToken } = bayonet;

          showBackdrop();

          if (payment_gateway === 'ORKESTA_PAY') {
            const { token, deviceInfo, error } = await createToken();
            if (error) {
              closeModal();
              confirmBackCode(error.code, null);
              return;
            }
            payToken = token.payment_method_id;
            sessionId = deviceInfo.device_session_id;
          } else {
            const response = await getSpredlyToken(
              payment['Gateway::Banwire'].creditCard,
              currency === 'MXN',
            );

            if (!response || response.error) {
              closeModal();
              window.Spreedly.removeHandlers();
              confirmBackCode(t(response.error), null);
              return;
            }

            payToken = response;
            sessionId = deviceSessionId;
          }

          const resp = await paymentCardOrder(
            payToken,
            sessionId,
            payment_gateway,
            bayonetFingerprintToken,
          );
          if (resp.error) {
            if (payment_gateway === 'ADYEN_PAY')
              window.Spreedly.removeHandlers();
            closeModal();
            if (
              resp.error.status === 409 &&
              resp.error.data.code === 'ADDON_NOT_AVAILABLE'
            ) {
              throw resp.error.data.code;
            }
            confirmBackCode(resp.error.data.code, resp.error.data.token);
            return;
          }

          const payment = await checkPaymentStatus(); // status:  completed | notCompleted | need3dsChallenge

          // We implemented the paymentStatus state as a side-effect dependency.
          // It is used to explicitly launch an effect (or function) that relies on
          // the latest state.payment data, guaranteeing it only runs *after* state.payment changes.
          // Without it, the dependent function would execute using state.payment's value
          // from the previous render cycle (stale data).
          setPaymentStatus(payment);
        }

        if (payment['Gateway::Oxxo']) {
          if (summary.total_to_pay > 10000) {
            return alert('No se puede pagar más de 10 mil pesos con Oxxo');
          }
          const resp = await paymentOxxoOrder();

          if (resp?.error) {
            if (
              resp.error.status === 409 &&
              resp.error.data.code === 'ADDON_NOT_AVAILABLE'
            ) {
              throw resp.error.data.code;
            }
            confirmBackCode(resp.error.data.code, resp.error.data.token);
            return;
          }

          sendPuchaseEvent(true);
          purchaseTrackingEvent({ type: 'paid', method: 'oxxo' });
          nextStep();
        }
        if (payment['PaymentMethod::Cash']) {
          const resp = await paymentCashOrder();

          if (resp?.error) {
            if (
              resp.error.status === 409 &&
              resp.error.data.code === 'ADDON_NOT_AVAILABLE'
            ) {
              throw resp.error.data.code;
            }
            confirmBackCode(resp.error.data.code, resp.error.data.token);
            return;
          }

          sendPuchaseEvent();
          purchaseTrackingEvent({ type: 'paid', method: 'cash' });
          nextStep();
        }
      } catch (e) {
        closeModal();
        if (e === 'ADDON_NOT_AVAILABLE') {
          handleTicketInsuranceHealthy();
          return;
        }
        return alert(e.error || 'Ocurrió un error al intentar pagar.');
      }
    }
  };

  useEffect(() => {
    if (isInChallenge && router.pathname === '/payment') {
      returnedFromBank();
      confirmChallenge(router.query?.order ? 'complete' : 'aborting');
    }
  }, [router.query?.order]);

  useEffect(() => {
    if (paymentStatus) {
      verifyPaymentStatusResponse(paymentStatus, 'onPayment');
    }
  }, [paymentStatus]);

  return {
    payBooking,
    payFreeOrder,
  };
}
